import React, { useState, useRef } from 'react';
import {
  TouchableOpacity,
  View,
  Text,
  StyleSheet,
  TextInput,
  Animated,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { goBack, navigate } from '../../navigation/RootNavigation';
import { useDispatch, useSelector } from 'react-redux';
import { removeAuthToken } from '../../redux/action/authActions';
import { COLORS, LAYOUT, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

/* ── Initials helper ─────────────────────────────────── */
const getInitials = (firstName = '', lastName = '') => {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f.substring(0, 2).toUpperCase();
  return 'U';
};

/* ── Avatar colours – deterministic from name ────────── */
const AVATAR_PALETTES = [
  { bg: '#6D08C0', text: '#fff' },
  { bg: '#0f62fe', text: '#fff' },
  { bg: '#0e7490', text: '#fff' },
  { bg: '#7c3aed', text: '#fff' },
  { bg: '#b45309', text: '#fff' },
  { bg: '#15803d', text: '#fff' },
  { bg: '#be123c', text: '#fff' },
  { bg: '#1d4ed8', text: '#fff' },
];
const getPalette = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
};

/* ── Custom Logout Modal ─────────────────────────────── */
const LogoutModal = ({ visible, userName, initials, palette, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <View style={lm.portalRoot}>
      {/* Backdrop */}
      <TouchableOpacity
        style={StyleSheet.absoluteFillObject}
        activeOpacity={1}
        onPress={onCancel}
      />

      {/* Card */}
      <View style={lm.card}>

        {/* Icon */}
        <View style={lm.iconWrap}>
          <Ionicons name="log-out-outline" size={28} color="#ef4444" />
        </View>

        <Text style={lm.title}>Log out?</Text>
        <Text style={lm.subtitle}>
          You'll be signed out of your account.{'\n'}You can log back in anytime.
        </Text>

        {/* Actions */}
        <TouchableOpacity style={lm.logoutBtn} onPress={onConfirm} activeOpacity={0.85}>
          <Ionicons name="log-out-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
          <Text style={lm.logoutBtnText}>Yes, Log Out</Text>
        </TouchableOpacity>

        <TouchableOpacity style={lm.cancelBtn} onPress={onCancel} activeOpacity={0.75}>
          <Text style={lm.cancelBtnText}>Stay Signed In</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const lm = StyleSheet.create({
  backdrop: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  portalRoot: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 99999,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },

  card: {
    width: 320,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 28,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 20,
    zIndex: 100000,
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  avatarText: {
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: 1,
  },
  name: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 16,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: '#f0f0f5',
    marginBottom: 20,
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#fee2e2',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1a1a2e',
    marginBottom: 8,
    fontFamily: 'poppins',
  },
  subtitle: {
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
    fontFamily: 'poppins',
  },
  logoutBtn: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ef4444',
    borderRadius: 30,
    paddingVertical: 13,
    marginBottom: 10,
  },
  logoutBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'poppins',
  },
  cancelBtn: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 30,
    borderWidth: 1.5,
    borderColor: '#e5e7eb',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#555',
    fontFamily: 'poppins',
  },
});

/* ── Main StatusBar component ────────────────────────── */
const StatusBar = ({ backbutton, title, navigation: navProp, backNavigation }) => {
  const userToken   = useSelector((state) => state?.AppReducer?.token);
  const userProfile = useSelector((state) => state?.Login?.userDetails);
  const dispatch    = useDispatch();

  const [logoutVisible, setLogoutVisible] = useState(false);
  const [avatarMenuVisible, setAvatarMenuVisible] = useState(false);

  const firstName = userProfile?.first_name || '';
  const lastName  = userProfile?.last_name  || '';
  const fullName  = `${firstName} ${lastName}`.trim();
  const initials  = getInitials(firstName, lastName);
  const palette   = getPalette(fullName || 'User');

  const handleLogout = () => {
    setLogoutVisible(false);
    dispatch(removeAuthToken());
    navigate('login');
  };

  return (
    <>
      <View style={styles.container}>
        {/* Left: back button */}
        <View style={styles.leftSection}>
          {backbutton ? (
            <TouchableOpacity
              style={styles.backButton}
              onPress={() =>
                backNavigation ? navigate(backNavigation) : navProp ? navProp.goBack() : goBack()
              }
              activeOpacity={0.7}
            >
              <Ionicons name="chevron-back" size={18} color={COLORS.primary} />
              <Text style={styles.backText}>
                {backNavigation === 'CurrentOnboards' ? 'Back To Current Tenancies' : 'Back'}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>

        {/* Right: avatar */}
        {userToken ? (
          <View style={styles.rightSection}>
            {/* Avatar — shows initials with dynamic colour */}
            <TouchableOpacity
              style={[styles.avatarCircle, { backgroundColor: COLORS.primary }]}
              onPress={() => setAvatarMenuVisible((v) => !v)}
              activeOpacity={0.85}
            >
              <Text style={[styles.avatarText, { color: palette.text }]}>{initials}</Text>
            </TouchableOpacity>

            {/* Avatar dropdown */}
            {avatarMenuVisible && (
              <>
                <TouchableOpacity
                  style={styles.dropdownOverlay}
                  activeOpacity={1}
                  onPress={() => setAvatarMenuVisible(false)}
                />
                <View style={styles.avatarDropdown}>
                  {/* User info at top of dropdown */}
                  <View style={styles.dropdownHeader}>
                    <View style={[styles.dropdownAvatar, { backgroundColor: COLORS.primary }]}>
                      <Text style={[styles.dropdownAvatarText, { color: palette.text }]}>{initials}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      {fullName ? (
                        <Text style={styles.dropdownName} numberOfLines={1}>{fullName}</Text>
                      ) : null}
                      <Text style={styles.dropdownRole} numberOfLines={1}>Landlord</Text>
                    </View>
                  </View>
                  <View style={styles.dropdownDivider} />

                  <TouchableOpacity
                    style={styles.dropdownItem}
                    onPress={() => { setAvatarMenuVisible(false); navigate('MyAccount'); }}
                  >
                    <Ionicons name="person-outline" size={15} color={COLORS.textSecondary} />
                    <Text style={styles.dropdownItemText}>My Account</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.dropdownItem}
                    onPress={() => { setAvatarMenuVisible(false); navigate('Setting'); }}
                  >
                    <Ionicons name="settings-outline" size={15} color={COLORS.textSecondary} />
                    <Text style={styles.dropdownItemText}>Settings</Text>
                  </TouchableOpacity>

                  <View style={styles.dropdownDivider} />

                  <TouchableOpacity
                    style={styles.dropdownItem}
                    onPress={() => { setAvatarMenuVisible(false); setLogoutVisible(true); }}
                  >
                    <Ionicons name="log-out-outline" size={15} color={COLORS.danger} />
                    <Text style={[styles.dropdownItemText, { color: COLORS.danger }]}>Logout</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        ) : null}
      </View>

      {/* Custom logout modal */}
      <LogoutModal
        visible={logoutVisible}
        userName={fullName}
        initials={initials}
        palette={palette}
        onConfirm={handleLogout}
        onCancel={() => setLogoutVisible(false)}
      />
    </>
  );
};

/* ── Styles ──────────────────────────────────────────── */
const styles = StyleSheet.create({
  container: {
    height: LAYOUT.topBarHeight,
    backgroundColor: COLORS.surface,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    zIndex: 10,
  },
  leftSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingRight: 12,
  },
  backText: {
    color: COLORS.primary,
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 2,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.surfaceHover || '#f5f5f9',
    borderRadius: BORDER_RADIUS.lg || 10,
    borderWidth: 1,
    borderColor: COLORS.primary,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'web' ? 6 : 8,
    maxWidth: 380,
    overflow: 'hidden',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: COLORS.textPrimary,
    paddingVertical: 0,
    outlineStyle: 'none',
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    position: 'relative',
  },
  iconBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: COLORS.surfaceHover,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  iconBtnActive: {
    backgroundColor: '#f0e8ff',
    borderColor: COLORS.primary,
  },
  notifDot: {
    position: 'absolute',
    top: -1, right: -1,
    width: 8, height: 8,
    borderRadius: 4,
    backgroundColor: COLORS.danger,
    borderWidth: 1.5,
    borderColor: COLORS.surface,
  },
  /* Avatar */
  avatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.18,
    shadowRadius: 4,
    elevation: 3,
  },
  avatarText: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  /* Dropdown */
  dropdownOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 98,
  },
  avatarDropdown: {
    position: 'absolute',
    top: 46,
    right: 0,
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    paddingVertical: 6,
    minWidth: 200,
    zIndex: 99,
    ...SHADOWS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  dropdownHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  dropdownAvatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dropdownAvatarText: {
    fontSize: 12,
    fontWeight: '800',
  },
  dropdownName: {
    fontSize: 13,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  dropdownRole: {
    fontSize: 11,
    color: COLORS.textSecondary,
    marginTop: 1,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 8,
  },
  dropdownItemText: {
    fontSize: 13,
    fontWeight: '500',
    color: COLORS.textPrimary,
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: COLORS.border,
    marginVertical: 4,
    marginHorizontal: 10,
  },
});

export default StatusBar;
